/**
 * Videos list reducer.
 *
 * Custom (not `createListReducer`) because the Dailymotion API has no reliable
 * total and we use a "load more" pattern: page 1 REPLACES the list, subsequent
 * pages APPEND. State is paginated on `hasMore` only.
 *
 * Stale-response guard: each request carries the signature of its query
 * (search + sort). The reducer tracks the signature of the latest page-1 fetch
 * and ignores any fulfilled response whose signature no longer matches — this
 * prevents a late "load more" reply from being appended to a different list
 * after the user changed the search or sort.
 */
export interface IDmVideo {
  id: string;
  title: string;
  description: string;
  duration: number;
  created_time: number;
  views_total: number;
  thumbnail_360_url: string;
  private: boolean;
  owner: string;
  'owner.screenname'?: string;
  allow_embed?: boolean;
}

export interface IVideosListState {
  list: IDmVideo[];
  page: number;
  hasMore: boolean;
  fetching: boolean;
  fetchingMore: boolean;
  error: number | string | null;
  signature: string | null;
}

const INITIAL_STATE: IVideosListState = {
  list: [],
  page: 1,
  hasMore: false,
  fetching: false,
  fetchingMore: false,
  error: null,
  signature: null,
};

export default function videos(
  state: IVideosListState = INITIAL_STATE,
  action: { type: string; payload?: any }
): IVideosListState {
  switch (action.type) {
    case 'FETCH_VIDEOS':
      // A new page-1 fetch defines the current query signature.
      return { ...state, fetching: true, error: null, signature: action.payload.signature };
    case 'FETCH_VIDEOS_FULFILLED':
      if (action.payload.signature !== state.signature) {
        return state; // stale page-1 response, a newer query already superseded it
      }
      return {
        ...state,
        fetching: false,
        error: null,
        list: action.payload.list,
        page: 1,
        hasMore: action.payload.hasMore,
      };
    case 'FETCH_VIDEOS_REJECTED':
      if (action.payload.signature !== state.signature) {
        return state;
      }
      // Drop the previous query's list: keeping it would show stale results
      // (and let "load more" append this query's page 2 to them).
      return { ...state, fetching: false, error: action.payload.status, list: [], page: 1, hasMore: false };
    case 'FETCH_VIDEOS_MORE':
      return { ...state, fetchingMore: true, error: null };
    case 'FETCH_VIDEOS_MORE_FULFILLED':
      if (action.payload.signature !== state.signature) {
        // The query changed while this page was in flight: drop it, don't append.
        return { ...state, fetchingMore: false };
      }
      return {
        ...state,
        fetchingMore: false,
        list: [...state.list, ...action.payload.list],
        page: action.payload.page,
        hasMore: action.payload.hasMore,
      };
    case 'FETCH_VIDEOS_MORE_REJECTED':
      if (action.payload.signature !== state.signature) {
        return { ...state, fetchingMore: false }; // stale failure of a previous query
      }
      return { ...state, fetchingMore: false, error: action.payload.status };
    default:
      return state;
  }
}
